import { Stack, Tooltip } from '@mui/material';
import { ReactNode } from 'react';
import Iconify from '@/components/Iconify';
import { PTypographyLoading } from '@/components/molecules/TypographyLoading/PTypographyLoading';
import {
  ICON_SIZE,
  META_ITEM_LOADER_WIDTH,
  META_ITEM_VALUE_MAX_WIDTH,
} from '@/components/organisms/PageHeader/constants';
import { META_ITEM_ICONS, PageHeaderMetaItemType } from '@/components/organisms/PageHeader/metaItemIcons';
import { useIsMetaLoading } from '@/components/organisms/PageHeader/metaLoadingContext';

export type PageHeaderMetaItemProps = {
  /**
   * Determines the icon rendered at the start of the item. The icon is always
   * visible, even while loading. See ADR-005.
   */
  type: PageHeaderMetaItemType;
  /**
   * Visible content of the item, e.g. "2,300 m²" or the building address.
   */
  value: ReactNode;
  /**
   * Tooltip text shown on hover. Always rendered — every item has a tooltip
   * to clarify the semantic meaning of its icon.
   */
  label: string;
  /**
   * Per-item loading override. When set, wins over the cascade from
   * `PageHeader.Meta`. When the item is loading, the icon and layout remain;
   * only the value is replaced by a skeleton.
   */
  isLoading?: boolean;
};

export const PageHeaderMetaItem = ({ type, value, label, isLoading }: PageHeaderMetaItemProps) => {
  const isCascadedLoading = useIsMetaLoading();
  const isEffectiveLoading = isLoading ?? isCascadedLoading;
  const icon = META_ITEM_ICONS[type];

  return (
    <Tooltip title={label} arrow>
      <Stack direction="row" alignItems="center" gap={0.5} sx={{ flex: '0 0 auto', color: 'text.secondary', pr: 2 }}>
        <Iconify icon={icon} width={ICON_SIZE} height={ICON_SIZE} />
        <PTypographyLoading
          variant="caption"
          color="secondary"
          isLoading={isEffectiveLoading}
          loaderWidth={META_ITEM_LOADER_WIDTH}
          noOverflow={{ maxWidth: META_ITEM_VALUE_MAX_WIDTH, title: '' }}
          sx={{ mt: 0.25 }}
        >
          {value}
        </PTypographyLoading>
      </Stack>
    </Tooltip>
  );
};
